import { z } from "zod";
import { AccessError, principalSchema } from "@proof/authorization";
import type { Principal } from "./repository";
import {
  hostJsonTransport,
  HostHttpError,
  type HostHttpOptions,
} from "./host-http";
export interface HttpIdentityOptions extends HostHttpOptions {
  /** Service credential, distinct from the requesting user's bearer token. */
  bearerToken: string;
}
const tokenSchema = z
  .string()
  .min(1)
  .max(8192)
  .regex(/^[A-Za-z0-9\-._~+/]+=*$/);
const responseSchema = z.discriminatedUnion("active", [
  z.object({ version: z.literal(1), active: z.literal(false) }).strict(),
  z
    .object({
      version: z.literal(1),
      active: z.literal(true),
      principal: principalSchema.refine(
        (p) => p.authorization === undefined && p.operatorContext === undefined,
      ),
    })
    .strict(),
]);
export function httpHostIdentityProvider(options: HttpIdentityOptions) {
  let transport: ReturnType<typeof hostJsonTransport>;
  try {
    if (!tokenSchema.safeParse(options.bearerToken).success) throw new Error();
    transport = hostJsonTransport(options, 65536);
  } catch {
    throw new AccessError("HOST_IDENTITY_CONFIGURATION_INVALID", 500);
  }
  const lookup = async (
    input: unknown,
    signal: AbortSignal,
  ): Promise<Principal> => {
    try {
      const response = responseSchema.safeParse(await transport(input, signal));
      if (!response.success)
        throw new AccessError("AUTHENTICATION_UNAVAILABLE", 503);
      if (!response.data.active)
        throw new AccessError("AUTHENTICATION_REQUIRED", 401);
      return response.data.principal;
    } catch (error) {
      if (error instanceof AccessError) throw error;
      if (error instanceof HostHttpError && error.reason === "cancelled")
        throw new AccessError("AUTHENTICATION_CANCELLED", 499);
      throw new AccessError("AUTHENTICATION_UNAVAILABLE", 503);
    }
  };
  return {
    authenticate(token: string, signal: AbortSignal) {
      const checked = tokenSchema.safeParse(token);
      if (!checked.success)
        return Promise.reject(new AccessError("AUTHENTICATION_REQUIRED", 401));
      return lookup(
        { version: 1, operation: "authenticate", token: checked.data },
        signal,
      );
    },
    async revalidate(
      expected: Principal,
      signal = new AbortController().signal,
    ) {
      const checked = principalSchema.safeParse(expected);
      if (!checked.success)
        throw new AccessError("AUTHENTICATION_UNAVAILABLE", 503);
      const { tenantId, subject } = checked.data;
      const current = await lookup(
        { version: 1, operation: "revalidate", tenantId, subjectId: subject },
        signal,
      );
      if (current.tenantId !== tenantId || current.subject !== subject)
        throw new AccessError("AUTHENTICATION_UNAVAILABLE", 503);
      return current;
    },
  };
}
